import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertCrewOrAdmin } from "@/lib/tenants.functions";
import {
  DISPOSABLE_DOMAINS,
  MIN_FILL_MS,
  RATE_LIMIT_MAX,
  RATE_LIMIT_WINDOW_MS,
} from "@/lib/waitlist.constants";
import { GROUP_SESSION_TYPES, type GroupSessionType } from "@/lib/group-sessions";

const OWNER_EMAIL = "micaiahf@gmail.com";
const SITE_URL = "https://squadflight.com";

const requestSchema = z.object({
  sessionType: z.enum(["academy", "open_air", "corporate"]),
  preferredAt: z.string().min(1).max(40),
  backupAt: z.string().max(40).optional().nullable(),
  contactName: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(30).optional().nullable(),
  pilotNames: z.array(z.string().trim().max(60)).max(4).default([]),
  notes: z.string().trim().max(1500).optional().nullable(),
  company: z.string().optional().nullable(), // honeypot
  elapsedMs: z.number().int().nonnegative(),
});

function toIso(v: string | null | undefined): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function laTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/Los_Angeles",
  });
}

/** Public: a group asks for a time. Crew confirm it and send the $299 link. */
export const requestGroupSession = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => {
    const parsed = requestSchema.safeParse(data);
    if (!parsed.success) throw new Error("Please check your details and try again.");
    return parsed.data;
  })
  .handler(async ({ data }) => {
    if (data.company && data.company.trim()) return { ok: true };
    if (data.elapsedMs < MIN_FILL_MS) {
      throw new Error("That submission looked automated. Please try again.");
    }
    const email = data.email.toLowerCase();
    if (DISPOSABLE_DOMAINS.has(email.split("@")[1] ?? "")) {
      throw new Error("Please use a permanent email address.");
    }
    const preferredAt = toIso(data.preferredAt);
    if (!preferredAt || new Date(preferredAt).getTime() < Date.now()) {
      throw new Error("Please pick a preferred time in the future.");
    }

    const headers = getRequest().headers;
    const ip =
      headers.get("cf-connecting-ip") ?? headers.get("x-forwarded-for")?.split(",")[0]?.trim();

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { defaultTenantId } = await import("@/lib/tenants.functions");
    const tenantId = await defaultTenantId(supabaseAdmin);

    if (ip) {
      const since = new Date(Date.now() - RATE_LIMIT_WINDOW_MS).toISOString();
      const { count } = await supabaseAdmin
        .from("signup_attempts")
        .select("id", { count: "exact", head: true })
        .eq("ip", ip)
        .gte("created_at", since);
      if ((count ?? 0) >= RATE_LIMIT_MAX) {
        throw new Error("Too many requests from this connection. Try again later.");
      }
      await supabaseAdmin.from("signup_attempts").insert({ ip, tenant_id: tenantId } as never);
    }

    // Link to an existing pilot/lead when we already know them.
    const { data: pilot } = await supabaseAdmin
      .from("pilots")
      .select("id")
      .eq("email_normalized", email)
      .eq("tenant_id", tenantId)
      .is("archived_at", null)
      .maybeSingle();

    const backupAt = toIso(data.backupAt);
    const pilotNames = data.pilotNames.filter(Boolean);
    const { data: row, error } = await supabaseAdmin
      .from("group_session_requests" as never)
      .insert({
        tenant_id: tenantId,
        session_type: data.sessionType,
        preferred_at: preferredAt,
        backup_at: backupAt,
        contact_name: data.contactName,
        email,
        phone: data.phone || null,
        pilot_names: pilotNames,
        notes: data.notes || null,
        pilot_id: (pilot as { id: string } | null)?.id ?? null,
      } as never)
      .select("id")
      .single();
    if (error || !row) {
      console.error("group request insert failed", error);
      throw new Error("Could not send your request. Please try again.");
    }
    const id = (row as { id: string }).id;

    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    const typeLabel = GROUP_SESSION_TYPES[data.sessionType as GroupSessionType].name;
    const shared = {
      contactName: data.contactName,
      typeLabel,
      preferredLabel: laTime(preferredAt),
      backupLabel: backupAt ? laTime(backupAt) : null,
    };
    try {
      await sendTemplateEmail("group-session-received", email, {
        templateData: shared,
        idempotencyKey: `group-received-${id}`,
        replyTo: "hello@squadflight.com",
      });
    } catch (e) {
      console.error("group request confirmation failed", e);
    }
    try {
      await sendTemplateEmail("group-session-admin-alert", OWNER_EMAIL, {
        templateData: {
          ...shared,
          email,
          phone: data.phone || null,
          pilotNames: pilotNames.join(", ") || null,
          notes: data.notes || null,
          manageUrl: `${SITE_URL}/group-requests`,
        },
        idempotencyKey: `group-admin-${id}`,
        overrideFixedRecipient: true,
      });
    } catch (e) {
      console.error("group request owner alert failed", e);
    }
    return { ok: true };
  });

export type GroupRequestRow = {
  id: string;
  session_type: GroupSessionType;
  status: "new" | "confirmed" | "paid" | "done" | "declined";
  preferred_at: string | null;
  backup_at: string | null;
  contact_name: string;
  email: string;
  phone: string | null;
  pilot_names: string[];
  notes: string | null;
  confirmed_at: string | null;
  payment_link_url: string | null;
  crew_notes: string | null;
  created_at: string;
};

export const listGroupRequests = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertCrewOrAdmin(context);
    const { data, error } = await context.supabase
      .from("group_session_requests" as never)
      .select(
        "id, session_type, status, preferred_at, backup_at, contact_name, email, phone, pilot_names, notes, confirmed_at, payment_link_url, crew_notes, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) throw new Error("Could not load group requests.");
    return (data ?? []) as unknown as GroupRequestRow[];
  });

export const updateGroupRequestStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["new", "paid", "done", "declined"]),
        crewNotes: z.string().max(1500).optional(),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertCrewOrAdmin(context);
    const patch: Record<string, unknown> = { status: data.status };
    if (data.crewNotes !== undefined) patch["crew_notes"] = data.crewNotes;
    const { error } = await context.supabase
      .from("group_session_requests" as never)
      .update(patch as never)
      .eq("id", data.id);
    if (error) throw new Error("Could not update that request.");
    return { ok: true };
  });

/** Crew lock in a time and send the group its $299 payment link. */
export const confirmGroupRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        confirmedAt: z.string().min(1),
        paymentLinkUrl: z.string().trim().url().max(500),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertCrewOrAdmin(context);
    const confirmedAt = toIso(data.confirmedAt);
    if (!confirmedAt) throw new Error("Pick the confirmed date and time.");
    const { data: row, error } = await context.supabase
      .from("group_session_requests" as never)
      .update({
        status: "confirmed",
        confirmed_at: confirmedAt,
        payment_link_url: data.paymentLinkUrl,
      } as never)
      .eq("id", data.id)
      .select("id, contact_name, email, session_type")
      .single();
    if (error || !row) throw new Error("Could not confirm that request.");
    const r = row as {
      id: string;
      contact_name: string;
      email: string;
      session_type: GroupSessionType;
    };
    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    const result = await sendTemplateEmail("group-session-confirmed", r.email, {
      templateData: {
        contactName: r.contact_name,
        typeLabel: GROUP_SESSION_TYPES[r.session_type].name,
        whenLabel: laTime(confirmedAt),
        paymentLinkUrl: data.paymentLinkUrl,
      },
      // A re-confirm at a different time gets its own email; the same one never repeats.
      idempotencyKey: `group-confirmed-${r.id}-${confirmedAt}`,
      replyTo: "hello@squadflight.com",
    });
    return { ok: true, emailed: result.sent };
  });
